import { apiClient } from '@/lib/api/client'
import {
  PropertyInsert,
  PropertyUpdate,
  PropertyWithStaysProperty,
} from '@/types'
import { IPaginatedRepository, IPaginatedRepositoryResponse } from './types'

export class PropertyRepository
  implements
    IPaginatedRepository<
      PropertyWithStaysProperty,
      PropertyInsert,
      PropertyUpdate
    >
{
  async getAll(params?: {
    search?: string
    status?: 'active' | 'hidden' | 'inactive' | 'draft'
    sortBy?: string
    sortOrder?: 'asc' | 'desc'
    page?: number
    limit?: number
    owner_id?: string
    showDeleted?: boolean
  }): Promise<IPaginatedRepositoryResponse<PropertyWithStaysProperty>> {
    const queryParams = new URLSearchParams(
      params as Record<string, string>
    ).toString()
    return apiClient.invoke<
      IPaginatedRepositoryResponse<PropertyWithStaysProperty>
    >(`properties?${queryParams}`, {
      method: 'GET',
    })
  }

  async getById(id: string): Promise<PropertyWithStaysProperty | null> {
    return apiClient.invoke<PropertyWithStaysProperty | null>(
      `properties?id=${id}`,
      {
        method: 'GET',
      }
    )
  }

  async create(data: PropertyInsert): Promise<PropertyWithStaysProperty> {
    const { images, ...formDataWithoutImages } = data

    const bodyPayload = new FormData()
    const imagesMetadata = images
      .filter(({ file }) => !!file)
      .map(({ is_primary }) => ({
        is_primary,
      }))
    bodyPayload.append(
      'property',
      JSON.stringify(
        Object.assign({}, formDataWithoutImages, { imagesMetadata })
      )
    )
    images.forEach((image) => {
      if (image.file) {
        bodyPayload.append('images', image.file)
      }
    })
    return apiClient.invoke<PropertyWithStaysProperty>('properties', {
      method: 'POST',
      body: bodyPayload,
    })
  }

  async update(
    id: string,
    data: PropertyUpdate
  ): Promise<PropertyWithStaysProperty> {
    const { images, ...formDataWithoutImages } = data
    const bodyPayload = new FormData()
    const imagesMetadata = images
      .filter(({ file }) => !!file)
      .map(({ is_primary }) => ({
        is_primary,
      }))
    bodyPayload.append(
      'property',
      JSON.stringify(
        Object.assign({}, formDataWithoutImages, { imagesMetadata, id })
      )
    )
    images.forEach((image) => {
      if (image.file) {
        bodyPayload.append('images', image.file)
      }
    })
    return apiClient.invoke<PropertyWithStaysProperty>('properties', {
      method: 'PUT',
      body: bodyPayload,
    })
  }

  async delete(id: string): Promise<void> {
    await apiClient.invoke<void>(`properties?id=${id}`, {
      method: 'DELETE',
    })
  }

  async restore(id: string): Promise<void> {
    await apiClient.invoke<void>(`properties?id=${id}&restore=true`, {
      method: 'PATCH',
    })
  }
}
